import {useState, useEffect, Fragment, useRef, useMemo, React} from "react";
import {getUserData, toLocaleDate} from "olympus-frontend/src/utility/Utils";
import {useTranslation} from "react-i18next";
import {Input, NumericTextBox} from "@progress/kendo-react-inputs";
import {DropDownButton, Button} from "@progress/kendo-react-buttons";
import debounce from "lodash/debounce";
import {
    EvolvereConfig,
    DemoFieldStream,
    DemoPlenitude,
    DemoBePower,
    OrionConfig
} from '../../configs/tenants/tenants'
import axios from "axios";
import appConfig from '../../configs/appConfig'
import {getComunications} from "../comunication/datasource";
import moment from 'moment';
import {KendoFilterTextDebounced, KendoNumericFilter} from "olympus-frontend/src/utility/kendo/utils";

const userPath = appConfig.API.usersAPI
const LOCAL_NGROK_HOST = process.env.REACT_APP_LOCAL_NGROK_HOST

// Repo toast convention (bottom-right, short autoclose). Pass `autoClose` in ms
// to override the default 1500 for errors that need more reading time.
export const toToastBottomRight = (autoClose = 1500) => ({
    position: "bottom-right", autoClose, hideProgressBar: true,
    closeOnClick: true, pauseOnHover: true, draggable: true, progress: undefined,
})

// react-select portal/menu styles: lift the dropdown above modals + kendo layers.
export const selectStyles = {
    menuPortal: (b) => ({...b, zIndex: 9999}),
    menu: (p) => ({...p, zIndex: 9999}),
}


export const updateComunicationBadge = async (t, i18n) => {
    const response_ = await setTimeoutMilliSeconds(350)
    const querySelector = document.querySelector(".main-menu-content > .navigation.navigation-main > li[data-id='comunication_list'] .badge")
    const querySelectorPopup = document.querySelector("#communications-alert")?.getAttribute('communication_ids');

    const response = await getComunications(t, i18n, {}, true, undefined, true)
    // See if there is a new notification, watching recipients.user.read_timestamp
    const newNotification = response.comunications
    const highComunication = newNotification.filter((x) => x.priority === 'HIGH')

    if (highComunication.length > 0) {
        const highComunicationIds = highComunication.map((x) => x.id)
        document.querySelector("#communications-alert").setAttribute('communication_ids', highComunicationIds.join(','))
        document.querySelector("#communications-alert").classList.remove("invisible")
    } else {
        document.querySelector("#communications-alert").setAttribute('communication_ids', '')
        document.querySelector("#communications-alert").classList.add("invisible")
    }

    if (!querySelector)
        return;
    if (newNotification.length > 0) {
        // Target the .main-menu-content > .navigation.navigation-main > li#comunication > .badge
        // Set it up to show and its text to the number of new notifications with DOM
        querySelector.style.display = "block"
        if (newNotification.length > 99)
            querySelector.innerText = "99+"
        else
            querySelector.innerText = newNotification.length
    } else {
        // Target the .main-menu-content > .navigation.navigation-main > li#comunication > .badge
        // Set it to hide with DOM
        const querySelectorEl = document.querySelector(".main-menu-content > .navigation.navigation-main > li[data-id='comunication_list'] .badge")
        if (querySelectorEl) {
            querySelectorEl.style.display = "none"
            querySelectorEl.innerText = "0"
        }
    }

}

/**
 * KendoReact ExportExcel utility:
 * @param dataSourceAPI: The given API for fetching data, it will be called on demand
 * @param dataSourceArgs: given API parameters as arrayOf(any)
 * @param columns: given Excel columns to export to,
 * @param _export: export Reference (useRef) to KendoExcel
 * @param dataSourceResponseKey: if the given API response must be parsed
 * @returns {Promise<*>}
 */
export const getExportData = async (dataSourceAPI, dataSourceArgs, columns, _export, dataSourceResponseKey) => {
    try {
        if (!_export.current)
            return Promise.reject("UNMOUNT")

        let response_ = await dataSourceAPI.apply(null, dataSourceArgs)

        // Handle date columns: convert ISO strings to Date objects and set export width
        // (Kendo ExcelExport ignores cellOptions.format on string values)
        const dateFields = new Set(
            columns.filter(col => ['date', 'datetime'].includes(col.type?.toLowerCase())).map(col => col.field)
        )
        const processedColumns = dateFields.size > 0
            ? columns.map(col => dateFields.has(col.field) ? {...col, width: col.exportWidth || 180} : col)
            : columns
        let data = dataSourceResponseKey ? response_[dataSourceResponseKey] : response_
        if (dateFields.size > 0) {
            data = (Array.isArray(data) ? data : []).map(row => {
                const newRow = {...row}
                dateFields.forEach(field => {
                    if (newRow[field] && typeof newRow[field] === 'string') {
                        newRow[field] = new Date(newRow[field])
                    }
                })
                return newRow
            })
        }

        // Waiting for blob://uri
        let data_uri = await _export.current.toDataURL(data, processedColumns)
        return _export.current.saveFile(data_uri)
    } catch (e) {
        if (!_export || !_export.current)
            return Promise.reject("UNMOUNT")
        return Promise.reject(e)
    }
}

export const getUserGridColumns = async (userId = -1, tableName = 'string') => {
    try {
        let response = await axios.get(`${userPath}/${userId}/ui_config`)
        if (
            response.data !== null &&
            response.data.grids &&
            response.data.grids[tableName] &&
            response.data.grids[tableName].columns &&
            Array.isArray(response.data.grids[tableName].columns)
        ) {
            return Promise.resolve({
                columns: response.data.grids[tableName].columns,
                found: true
            })
        } else
            return Promise.resolve({
                columns: [],
                found: false
            })
    } catch (e) {
        return Promise.reject(new Error(`Exception Grid Columns for user:${userId}, gridname:${tableName} table, from API ${e}`))
    }
}

export const createFormData = (JSONObject) => {
    const params = new FormData();
    Object.keys(JSONObject).forEach((x) => {
        if (x === 'electronic_load_ids') {
            JSONObject[x].forEach((electronic_load_id) => params.append(x, electronic_load_id))
        } else if (x.includes('_json')) {
            params.append(x, JSON.stringify(JSONObject[x]))
        } else params.append(x, JSONObject[x])
    })
    return params
}

//TODO: Migrate to olympus-frontend
export const getReactSelectFormattedObject = (
    arrayOfElements,
    valueKey,
    valueLabel,
    missingKeyValue = false,
    extendWithProps = false,
    translateHook = null) => {
    // If array contains List[str] or Enums assign a integer id
    let transformedArray = []

    if (missingKeyValue) {
        let index = -1
        transformedArray = arrayOfElements.map((x) => {
            index += 1
            return (extendWithProps) ?
                {...x, value: index, label: x[valueLabel]} :
                {value: index, label: x[valueLabel]}
        })
    } else {
        transformedArray = arrayOfElements.map((x) => {
            return (extendWithProps) ?
                {...x, value: x[valueKey], label: x[valueLabel]} :
                {value: x[valueKey], label: x[valueLabel]}
        })
    }
    // Check if need translation
    if (translateHook)
        transformedArray = transformedArray.map((x) => {
            return {...x, label: translateHook(x.label)}
        })
    return transformedArray
}

/**
 Use the current url to get the current 'app' configuration from the folder configs/tenants/{tenantName}
 Behaviour:
 1. Get from url the tenant name (e.g. https://hermes.m2d.tech, https://hermes-sea.m2d.tech)
 2. Get the tenant configuration from the folder configs/tenants/{tenantName}
 3. Return the tenant configuration to the App component
 **/
export const resolvingTenant = () => {
    const url = window.location.hostname;
    const tenantDomain = `${window.location.protocol}//${window.location.host}`

    // If development
    if (
        ['http://localhost', '192.168.1.41', 'localhost', '127.0.0.1', '0.0.0.0', LOCAL_NGROK_HOST].filter(
            (localhost_like_str) => localhost_like_str && window.location.hostname.includes(localhost_like_str)
        ).length > 0
    ) {
        // Get Evolvere as default
        return EvolvereConfig(tenantDomain).app
    }
    // FieldStream Case, excluded from Hermes tenants
    if (url.includes('demo.fieldstream.pro'))
        return DemoFieldStream(tenantDomain).app

    const tenantName = url.split('.m2d.tech')[0]
    switch (tenantName) {
        case "hermes":
        case "hermes-dev":
        case "hermes-acc":
        case LOCAL_NGROK_HOST:
            return EvolvereConfig(tenantDomain).app
        case "hermes-demo":
            return OrionConfig(tenantDomain).app
        case "hermes-plenitude-demo":
            return DemoPlenitude(tenantDomain).app
        case "hermes-bepower-demo":
            return DemoBePower(tenantDomain).app
        default:
            return EvolvereConfig(tenantDomain).app
    }
}

export const getUserRole = () => {
    let userData = getUserData()
    if (userData.groups.length > 0 || userData.is_superuser)
        if (userData.groups.map((x) => x.name).includes('administrator') || userData.is_superuser) return 'admin'
        else if (userData.groups.map((x) => x.name).includes('planner')) return 'planner'
        else if (userData.groups.map((x) => x.name).includes('technician')) return 'technician'
        else return ''
    return ''
}
// TODO: Migrate su olympus-frontend, insert doc
export const setTimeoutMilliSeconds = async (timeoutMilliseconds) => {
    await new Promise(resolve => setTimeout(resolve, timeoutMilliseconds))
}

/**
 * Return olympus-fronend based validation errors [TRANSLATED].
 * it can handle also RAINBOW-CRM ERRORS
 * Rainbow Errors
 *   Status: 424
 *   Response Example: {"details":[{"code":7009,"message":"Il planning indicato non è presente a sistema"}]}
 * @param  {responseData} response from API handler.
 * @return {Array[ {name: '', msg: ''} ] || String }
 */
export const settingAPIValidationErrors = (responseData) => {
    // Risposte senza body (es. 503 senza payload) arrivano con responseData undefined:
    // niente errori strutturati da formattare, il chiamante userà il messaggio di fallback.
    if (!responseData) return [];
    const details = responseData.details || responseData.detail;
    let formatted_errors = []

    if (typeof details === 'string')
        return details

    if (Array.isArray(details) && details.every((x) => typeof x === 'string')) {
        return details.join(', ')
    }

    if (Array.isArray(details)) {
        // Build up formatted_errors objects
        formatted_errors = details.map((detailLocation) => {
            return {
                name: Array.isArray(detailLocation.loc) ? detailLocation.loc.join(".") : detailLocation.loc,
                platform: detailLocation.code ? 'Rainbow' : 'Hermes',
                msg: ''
            }
        })
        details.forEach((validationField) => {
            // Msg can be array
            const validationFieldName = Array.isArray(validationField.loc) ? validationField.loc.join(".") : validationField.loc;
            if (Array.isArray(validationField.msg)) {
                formatted_errors.find((detailLocation) =>
                    detailLocation.name === validationFieldName)
                    .msg = validationField.msg.join(', ')
            } else
                formatted_errors.find((detailLoc) =>
                    detailLoc.name === validationFieldName)
                    .msg = validationField.msg
        })
    }
    return formatted_errors.map((errors_) => {
        return {
            ...errors_,
            msg: errors_.platform === 'Rainbow' ? `Rainbow Error: ${errors_.msg}` : errors_.msg,
        }
    })
}

export const parseKendoGridDataState = (dataState, customQueryParams = undefined, parseFilterTextColumns = []) => {
    let string

    if (!customQueryParams)
        string = `?take=${dataState.take}&skip=${dataState.skip}`
    else
        string = `${customQueryParams}`
    if (dataState.filter) {
        if (parseFilterTextColumns.length > 0) {
            // Find if filters have a filter included into parseFilterTextColumns and if they have operator 'isnull' and value null
            // If yes, for each of one add another filter with operator 'eq' and value '' in logic OR with the previous one

            // Example1: {logic: "and", filters: [{field: "main_contractor.name", operator: "eq", value: 100 }]}
            // Result1: {logic: "or", filters: [{field: "main_contractor.name", operator: "eq", value: 100 }, {field: "main_contractor.name", operator: "eq", value: "EMPTY" }]}

            // Example2: {logic: "and", filters: [{field: "main_contractor.name", operator: "eq", value: 100 }, {field: "id", operator: "eq", value:1}]}
            // Result2: {logic: "and", filters: [{ logic: "or", filters: [{ field: "main_contractor.name", operator: "isnull", value: null },{ field: "main_contractor.name", operator: "eq", value: "EMPTY" }]},{field: "id", operator: "eq", value:1}]}

            // Example3: {logic: "and", filters: [{field: "region", operator: "eq", value: 100 }, {field: "id", operator: "eq", value:1}]}
            // Result3: {logic: "and", filters: [{ logic: "or", filters: [{ field: "region", operator: "isnull", value: null },{ field: "region", operator: "eq", value: "EMPTY" }]},{field: "id", operator: "eq", value:1}]}

            let filters_ = dataState.filter.filters

            if (filters_.length > 0) {
                for (let i = 0; i < filters_.length; i++) {
                    if (
                        parseFilterTextColumns.includes(filters_[i].field) &&
                        filters_[i].operator === 'isnull' &&
                        filters_[i].value === null
                    ) {
                        dataState.filter.filters[i] = {
                            logic: 'or',
                            filters: [
                                {
                                    field: filters_[i].field,
                                    operator: 'eq',
                                    value: ''
                                },
                                filters_[i]
                            ]
                        }
                    }
                }
            }
        }
        string += `&filter=${encodeURIComponent(JSON.stringify(dataState.filter))}`
    }
    if (dataState.sort)
        string += `&sort=${encodeURIComponent(JSON.stringify(dataState.sort))}`
    return string
}

export const checkEmptyFields = (dataState, translateHook) => {
    // Filters are done from backend, forward correct fields and values to ensure working well.
    // company_type is translated (FE purpose) => must be untranslated
    // company_type can be null (FE has its i18n value: Empty or Vuoto) => Translate
    // Relationship values with ".", if they are null are forwarded as foreign_key field
    let dataState_ = JSON.parse(JSON.stringify(dataState));
    if (!dataState_.filter || (Array.isArray(dataState_.filter.filters) && dataState_.filter.filters.length === 0))
        return dataState_
    let this_filter = dataState_.filter.filters
    if (this_filter) {
        do {
            this_filter = this_filter[0].filters
            if (Array.isArray(this_filter) && this_filter.length > 0 && !this_filter.filters) {
                // can substitute strings
                for (let i = 0; i < this_filter.length; i++) {
                    let this_element_filtered = this_filter[i]
                    // Check empty value and reformat to "null"
                    if (translateHook("EMPTY") === this_element_filtered.value) {
                        // Check if field is a relationship field
                        if (this_element_filtered.field.includes('.'))
                            this_element_filtered.field = this_element_filtered.field.split('.')[0]
                        this_element_filtered.value = null
                    }
                }
            }
        } while (Array.isArray(this_filter.filters) && this_filter.filters.length > 0)
    }
    return dataState_
}

// TODO: set datatestid for inputs on grids?

export const SET_GRID_ATTACHMENTS = (gridRef, baseValue = 'base', columnFields = []) => {

    if (gridRef.current) {
        let indexPages = 0
        const gridElement = gridRef.current.element
        const headerIcons = gridRef.current.headerRef.current.element.querySelectorAll('.k-grid-column-menu.k-grid-filter')
        const pageSizeIcons = gridRef.current.element.querySelectorAll('.k-pager-numbers-wrap .k-pager-numbers li')
        const pageSizeChooserDropdown = gridRef.current.element.querySelector('.k-pager-sizes .k-widget.k-dropdown')

        if (gridElement) gridElement.setAttribute('data-testid', `${baseValue}-grid`)
        if (pageSizeChooserDropdown) pageSizeChooserDropdown.setAttribute('data-testid', `${baseValue}-pageSize-dropdown`)
        // Must specify fields!
        if (headerIcons.length && columnFields.length > 0)
            headerIcons.forEach((node, indexNode) => {
                if (columnFields[indexNode] !== undefined)
                    node.setAttribute('data-testid', `${baseValue}-filter-${columnFields[indexNode]}`)
            })
        if (pageSizeIcons.length) pageSizeIcons.forEach(node => {
            indexPages += 1
            node.setAttribute('data-testid', `${baseValue}-pageSize-${indexPages}`)
        })
    }
}

export const KendoEqualsToTextFilter = (props) => {

    const {i18n, t} = useTranslation()
    const [operators, setOperators] = useState(() => {
        return [{text: t('Is equal to'), operator: 'eq'}]
    })
    const [selectedOperator, setSelectedOperator] = useState(operators[0])
    const buttonEl = useRef(null);

    const onChange = (event) => {
        if (event.target.value === null || event.target.value === '')
            buttonEl.current.click()
        else
            props.onChange({
                value: event.target.value,
                operator: selectedOperator.operator,
                syntheticEvent: event.syntheticEvent,
            });
    };

    const onClearButtonClick = (event) => {
        event.preventDefault();
        props.onChange({
            value: null,
            operator: '',
            syntheticEvent: event,
        });
    };

    const value = props.value;
    return (
        <div className={"d-flex"} style={{gap: '0.25rem'}}>
            <Input
                style={{width: '80%'}}
                value={value}
                onChange={onChange}
            />
            <div className={"filter-dropdowns d-flex"}>
                <DropDownButton
                    className="buttons-container-button"
                    items={operators}
                    item={(props) =>
                        <option
                            className={`${selectedOperator.text === props.item.text ? 'text-primary k-item' : 'k-item'}`}
                            value={props.item.text} {...props}>
                            {props.item.text}
                        </option>
                    }
                    icon="filter"
                    title={t("Choose Operator")}
                    textField="text"
                />
                <button
                    ref={buttonEl}
                    className="k-button k-button-icon k-clear-button-visible"
                    title={t("Clear2")}
                    onClick={onClearButtonClick}
                >
                    <span className="k-icon k-i-filter-clear"/>
                </button>
            </div>
        </div>
    );
};

export const KendoContainsTextFilter = (props) => {

    const {i18n, t} = useTranslation()
    const [operators, setOperators] = useState(() => {
        return [{text: t('Contains'), operator: 'contains'}]
    })
    const [selectedOperator, setSelectedOperator] = useState(operators[0])
    const buttonEl = useRef(null);

    const onChange = (event) => {
        if (event.target.value === null || event.target.value === '')
            buttonEl.current.click()
        else
            props.onChange({
                value: event.target.value,
                operator: selectedOperator.operator,
                syntheticEvent: event.syntheticEvent,
            });
    };

    const onClearButtonClick = (event) => {
        event.preventDefault();
        props.onChange({
            value: null,
            operator: '',
            syntheticEvent: event,
        });
    };

    const value = props.value;
    return (
        <div className={"d-flex"}>
            <Input
                style={{flex: 1}}
                value={value}
                onChange={onChange}
            />
            <div className={"filter-dropdowns d-flex"}>
                <button
                    ref={buttonEl}
                    className="k-button k-button-icon k-clear-button-visible"
                    style={{marginLeft: '0.3rem'}}
                    title={t("Clear2")}
                    onClick={onClearButtonClick}
                >
                    <span className="k-icon k-i-filter-clear"/>
                </button>
            </div>
        </div>
    );
};

// ** Generic Components for rendering Kendo Text Filter without declaring it as an arrow anonymous function
export const KendoFilterTextDebouncedList = (props) => (
    <KendoFilterTextDebounced {...props} isFieldTypeOfList={true}/>
)

export const KendoNumericFilterList = (props) => (
    <KendoNumericFilter {...props} isIntoAList={true}/>
)

// Numeric filter that allows configurable `min` (so negative values are accepted)
// and a `format` prop forwarded to NumericTextBox (e.g. 'p2' for percentage:
// the user types 5,32 and the underlying value sent to the backend is 0.0532).
export const KendoNumericFilterSigned = (props) => {

    const format = props.format ? props.format : '#'
    const min = props.min !== undefined ? props.min : null
    const {i18n, t} = useTranslation()
    const [operators, setOperators] = useState(() => {
        const listOperators = [
             {text: t('Is empty'), operator: 'isempty'},
             {text: t('Is not empty'), operator: 'isnotempty'}
        ]
        const defaultOperators = [
            {text: t('Is equal to'), operator: 'eq'},
            {text: t('Is not equal to'), operator: 'neq'},
            {text: t('Is greater than or equal to'), operator: 'gte'},
            {text: t('Is less than or equal to'), operator: 'lte'}
        ]
        if (props.isIntoAList)
            return [...defaultOperators, ...listOperators]
        else
            return [
                ...defaultOperators,
                {text: t('Is null'), operator: 'isnull'},
            ]
    })
    const [selectedOperator, setSelectedOperator] = useState(
        props.operator &&
        operators.find((x) => x.operator === props.operator) ?
            operators.find((x) => x.operator === props.operator) :
        operators[0]
    )

     // Remember the last value to avoid calling the onChange function
    const [cachedTextFilter, setCachedTextFilter] = useState('')

    useEffect(() => {
        // When the parent grid pushes back the emitted (scaled) value, reverse
        // the scale so the input keeps showing what the user typed (e.g. user
        // types 10,2 representing 10.2 % — display stays "10,2", not "0,102").
        let displayFromProp = props.value;
        if (
            props.valueScale !== undefined &&
            typeof displayFromProp === 'number' &&
            isFinite(displayFromProp) &&
            props.valueScale !== 0
        ) {
            displayFromProp = displayFromProp / props.valueScale;
            displayFromProp = Math.round(displayFromProp * 1e10) / 1e10;
        }
        if (displayFromProp !== cachedTextFilter)
            setCachedTextFilter(displayFromProp)
    }, [props.value])

    const buttonEl = useRef(null);

    const handleInputChange = (event) => {
        const displayValue = event.target.value;
        // Keep the input showing what the user typed (displayValue), but emit a
        // scaled + IEEE-cleaned number to the backend. `valueScale` is used for
        // percent fields rendered without the `%` symbol (scale 0.01).
        let emittedValue = displayValue;
        if (typeof emittedValue === 'number' && isFinite(emittedValue)) {
            if (props.valueScale !== undefined) {
                emittedValue = emittedValue * props.valueScale;
            }
            emittedValue = Math.round(emittedValue * 1e10) / 1e10;
        }
        setCachedTextFilter(displayValue);
        debouncedOnChange(emittedValue, event);
    };

    // Kendo's NumericTextBox onChange only fires on parsed-value changes, so
    // typing the decimal separator (e.g. "," after "1") does not reset the
    // debounce — the previously-scheduled fetch fires mid-edit. Re-arm the
    // debounce on every keystroke that does not already trigger onChange.
    const handleKeyDown = (event) => {
        if (cachedTextFilter === null || cachedTextFilter === '')
            return;
        let emittedValue = cachedTextFilter;
        if (typeof emittedValue === 'number' && isFinite(emittedValue)) {
            if (props.valueScale !== undefined) {
                emittedValue = emittedValue * props.valueScale;
            }
            emittedValue = Math.round(emittedValue * 1e10) / 1e10;
        }
        debouncedOnChange(emittedValue, {syntheticEvent: event});
    };

    const debouncedOnChange = useMemo(() => {
        return debounce((value, event) => {

            if (value === null || value === '') {
                onClearButtonClick(event.syntheticEvent);
            } else {
                if (['isempty', 'isnotempty', 'isnull'].includes(selectedOperator.operator) && value !== null && value !== '') {
                    setSelectedOperator(operators[0])
                    props.onChange({
                        value: value,
                        operator: operators[0].operator,
                        syntheticEvent: event.syntheticEvent
                    })
                } else
                    props.onChange({
                        value: value,
                        operator: selectedOperator.operator,
                        syntheticEvent: event.syntheticEvent
                    });
            }
        }, 750);
    }, [props.onChange, selectedOperator]);

    // Cleanup debounce event on unMount
    useEffect(() => {
        return () => {
            debouncedOnChange.cancel();
        };
    }, [debouncedOnChange]);

    const onChangeOperator = (event) => {
        setSelectedOperator(event.item)
        // For eq, neq, gte, lte ensure that the value is not null
        if (
            ['eq', 'neq', 'gte', 'lte'].includes(event.item.operator) &&
            (cachedTextFilter === null || cachedTextFilter === '')
        )
            return;

        if (event.item.operator === 'isnull' || event.item.operator === 'isempty' || event.item.operator === 'isnotempty') {
            props.onChange({
                value: null,
                operator: event.item.operator,
                syntheticEvent: event.syntheticEvent
            })
            setCachedTextFilter(null)
            return;
        }
        if (event.target.value === null) {
            props.onChange({
                value: null,
                operator: event.item.operator,
                syntheticEvent: event.syntheticEvent
            });
            setCachedTextFilter(null)
        } else {
            let emittedValue = cachedTextFilter;
            if (typeof emittedValue === 'number' && isFinite(emittedValue)) {
                if (props.valueScale !== undefined) {
                    emittedValue = emittedValue * props.valueScale;
                }
                emittedValue = Math.round(emittedValue * 1e10) / 1e10;
            }
            props.onChange({
                value: emittedValue,
                operator: event.item.operator,
                syntheticEvent: event.syntheticEvent
            })
        }
    }

    const onClearButtonClick = (event) => {
        event.preventDefault();
        props.onChange({
            value: null,
            operator: '',
            syntheticEvent: event,
        });
        setCachedTextFilter(null)
    };

    return (
        <div className={"d-flex"}>
                <NumericTextBox
                    width="100%"
                    value={cachedTextFilter}
                    min={min}
                    format={format}
                    decimals={props.decimals !== undefined ? props.decimals : undefined}
                    restrictDecimals={props.restrictDecimals !== undefined ? props.restrictDecimals : undefined}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                />
            <div className={"filter-dropdowns d-flex"} style={{marginLeft: '0.25rem'}}>
                <DropDownButton
                    className="buttons-container-button"
                    items={operators}
                    item={(props) =>
                        <option
                            className={`${selectedOperator.text === props.item.text ? 'text-primary k-item' : 'k-item'}`}
                            value={props.item.text} {...props}>
                            {props.item.text}
                        </option>
                    }
                    icon="filter"
                    title={t("Choose Operator")}
                    textField="text"
                    onItemClick={(evt) => onChangeOperator(evt)}
                />
                <Button
                    ref={buttonEl}
                    className="k-button k-button-icon k-clear-button-visible"
                    title={t("Clear2")}
                    style={{width: "33.5px"}}
                    onClick={onClearButtonClick}
                >
                    <span className="k-icon k-i-filter-clear"/>
                </Button>
            </div>
        </div>
    );
};


export const GridInjector = (props) => {
    const {
        baseGridName,
        gridReference,
        columnFields
    } = props

    // TODO: columns will be dynamics based on user preference
    useEffect(() => {
        setTimeout(() => {
            SET_GRID_ATTACHMENTS(gridReference, baseGridName, columnFields)
        }, 750)
    }, [])

    return (
        <Fragment/>
    )
}

export const getStrDayOfWeek = (weekday = -1, translateHook = undefined) => {
    let item = ''
    switch (weekday) {
        case 0:
            item = translateHook('Monday')
            break
        case 1:
            item = translateHook("Tuesday")
            break
        case 2:
            item = translateHook("Wednesday")
            break
        case 3:
            item = translateHook("Thursday")
            break
        case 4:
            item = translateHook("Friday")
            break
        case 5:
            item = translateHook("Saturday")
            break
    }
    return item
}

// Get the number of all days between (Monday - Friday) in a given year-month
export const getLVDaysInMonth = (year = 2024, month = 1) => {
    const monthDays_ = moment(`${year}-${month}-01`).daysInMonth()
    const days_ = Array.from({length: monthDays_}, (_, i) => i + 1)

    const workingDays_ = days_.filter(day => {
        const weekDay = moment(`${year}-${month}-${day}`).isoWeekday();
        return weekDay >= 1 && weekDay <= 5; // L-V
    }).length;

    return workingDays_
}

// Get the number of all days between (Sat - Sund) in a given year-month
export const getSDDaysInMonth = (year = 2024, month = 1) => {
    const monthDays_ = moment(`${year}-${month}-01`).daysInMonth();
    const days_ = Array.from({length: monthDays_}, (_, i) => i + 1);

    const workingDays_ = days_.filter(day => {
        const weekDay = moment(`${year}-${month}-${day}`).isoWeekday();
        return weekDay === 6 || weekDay === 7; // Saturday or Sunday
    }).length;

    return workingDays_;
}


// Rule for input form, check if the string representation of a number is equal or greater than 0 (case missing from the olympus rules)
export const DECIMAL_FIELD_WITH_SEPARATORS_RULES_NO_NEGATIVE = (translationHook, language = undefined, required = true) => {
    return {
        required: {
            value: required,
            message: translationHook("This field is required")
        },
        pattern: {
            value: (() => {
                if (language === 'it') {
                    return /^([0-9]{1,3}(\.[0-9]{3})*)(,[0-9]+)?$|^[0-9]+(,[0-9]+)?$/
                }
                return /^([0-9]{1,3}(,[0-9]{3})*)(\.[0-9]+)?$|^[0-9]+(\.[0-9]+)?$/
            })(),
            message: translationHook("Insert a valid float number, equal or greater than 0"),
        }
    }
}

export const DECIMAL_FIELD_WITH_SEPARATORS_RULES_POSITIVE = (translationHook, language = undefined, required = true) => {
    return {
        required: {
            value: required,
            message: translationHook("This field is required")
        },
        pattern: {
            value: (() => {
                if (language === 'it') {
                    return /^([1-9][0-9]{0,2}(\.[0-9]{3})*)(,[0-9]+)?$|^[0-9]+(,[0-9]+)?$/
                }
                return /^([1-9][0-9]{0,2}(,[0-9]{3})*)(\.[0-9]+)?$|^[0-9]+(\.[0-9]+)?$/
            })(),
            message: translationHook("Insert a valid float number, equal or greater than 0"),
        }
    }
}

export const DECIMAL_NUMBER_CONVERTER = (value, max = 2, language = undefined) => {
    if (typeof value !== "string" && typeof value !== "number") return NaN;
    let str = String(value).trim();

    // Gestione del segno negativo all'inizio
    const isNegative = str.startsWith("-");
    str = str.replace(/^-/, "");

    if (language === "it") {
        str = str.replace(/\./g, "").replace(/,/g, ".");
    } else {
        str = str.replace(/,/g, "");
    }

    let num = parseFloat(str);
    if (isNaN(num)) return NaN;
    if (isNegative) num = -num;
    return parseFloat(num.toFixed(max));
}

export const DECIMAL_NUMBER_RECONVERTER = (value, language = undefined) => {
    if (typeof value !== "string" && typeof value !== "number") return value;
    let str = String(value).trim();
    const isNegative = str.startsWith("-");
    str = str.replace(/^-/, "");

    if (language === "it") {
        str = str.replace(/\./g, "");
    } else {
        str = str.replace(/,/g, "");
    }

    let [integer, decimal] = language === "it"
        ? str.split(",")
        : str.split(".");

    integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, language === "it" ? "." : ",");

    let result = decimal !== undefined
        ? integer + (language === "it" ? "," : ".") + decimal
        : integer;

    return isNegative ? "-" + result : result;
}

export const DateCell = ({i18n, withoutTime = false, ...props}) => {
    let date = '';
    if (props.field.includes('.')) {
        date = props.dataItem[props.field.split('.')[0]][props.field.split('.')[1]] ?
            toLocaleDate(props.dataItem[props.field.split('.')[0]][props.field.split('.')[1]], i18n.language, withoutTime) : '';
    } else {
        date = props.dataItem[props.field] ? toLocaleDate(props.dataItem[props.field], i18n.language, withoutTime) : '';
    }
    return <td>{date}</td>
}

export const getDataStateDocumentFilters = (
    selectedFilters = {},
    categoryField = 'doc_category',
    titleField = 'doc_title',
    validField = 'doc_valid',
    inReviewField = 'doc_in_review',
    validUntilField = 'doc_valid_until'
) => {
    let dataState = {
        skip: 0,
        filter: {logic: 'and', filters: []}
    }

    if (selectedFilters.textFilter) {
        dataState.filter.filters.push({
            logic: 'or',
            filters: [
                {
                    field: titleField,
                    operator: 'contains',
                    value: selectedFilters.textFilter
                },
                {
                    field: categoryField,
                    operator: 'contains',
                    value: selectedFilters.textFilter
                }
            ]
        });
    }
    return dataState;
}

/**
 * Client-side filter for documents with three mutually exclusive states
 *
 * LOGIC:
 * Documents have three possible states:
 * 1. VALID (✓) = valid AND NOT in review
 * 2. NOT_VALID (✗) = invalid AND NOT in review
 * 3. IN_REVIEW (?) = in review (regardless of valid/invalid)
 *
 * Filter behavior:
 * ✓ alone = valid docs NOT in review
 * ✗ alone = invalid docs NOT in review
 * ? alone = ALL docs in review (any validity)
 * ✓✗ = all docs NOT in review (both valid and invalid)
 * ✓? = valid NOT in review + ALL in review
 * ✗? = invalid NOT in review + ALL in review
 * ✓✗? = everything (all three states)
 * (none) = nothing shown
 *
 * @param documents - Array of documents to filter
 * @param selectedFilters - Object with statusFilter array
 * @param validField - Field name for validity (default: 'doc_valid')
 * @param inReviewField - Field name for review status (default: 'doc_in_review')
 * @returns Filtered array of documents
 */
export const filterDocumentsClientSide = (
    documents = [],
    selectedFilters = {},
    validField = 'doc_valid',
    inReviewField = 'doc_in_review'
) => {
    if (!selectedFilters.statusFilter || selectedFilters.statusFilter.length === 0) {
        return [];
    }

    const hasValid = selectedFilters.statusFilter.includes('VALID');
    const hasNotValid = selectedFilters.statusFilter.includes('NOT_VALID');
    const hasInReview = selectedFilters.statusFilter.includes('IN_REVIEW');

    return documents.filter(doc => {
        const isValid = doc[validField] === true;
        const isInvalid = doc[validField] === false;
        const isInReview = doc[inReviewField] === true;

        // Determine document's state (mutually exclusive)
        if (isInReview) {
            // State: IN_REVIEW (regardless of validity)
            return hasInReview;
        } else if (isValid) {
            // State: VALID (valid AND NOT in review)
            return hasValid;
        } else {
            // State: NOT_VALID (invalid AND NOT in review)
            return hasNotValid;
        }
    });
}

/**
 * Sorts documents by category (alphabetically) and then by title (alphabetically) within each category.
 * This ensures the same ordering in both sidebar and card views.
 *
 * @param documents - Array of documents to sort
 * @param categoryField - Field name for category (default: 'doc_category')
 * @param titleField - Field name for title (default: 'doc_title')
 * @returns Sorted array of documents
 */
export const sortDocumentsByCategoryAndTitle = (
    documents = [],
    categoryField = 'doc_category',
    titleField = 'doc_title'
) => {
    // Group documents by category
    const categoryMap = {};
    documents.forEach(doc => {
        const category = doc[categoryField] || '';
        if (!categoryMap[category]) {
            categoryMap[category] = [];
        }
        categoryMap[category].push(doc);
    });

    if (Object.keys(categoryMap).length === 0) {
        return [];
    }
    // Sort categories alphabetically
    const sortedCategories = Object.keys(categoryMap).sort();

    // Sort documents within each category by title, then flatten
    return sortedCategories.flatMap(category =>
        categoryMap[category].sort((a, b) => {
            const titleA = (a[titleField] || '').toLowerCase();
            const titleB = (b[titleField] || '').toLowerCase();
            return titleA.localeCompare(titleB);
        })
    );
}

/**
 * Client-side filter for teams with independent finalize and authorization status
 *
 * Filter logic:
 * - Text filter: searches team name AND member names
 * - Status filter: independent AND logic for finalize and authorization states
 *   (not priority-based like documents - both conditions must match)
 *
 * @param {Array} teams - Array of teams to filter
 * @param {Object} selectedFilters - Object with textFilter and statusFilter properties
 * @returns {Array} Filtered array of teams with search match metadata (_searchMatch)
 */
export const filterTeamsClientSide = (teams = [], selectedFilters = {}) => {
    // If no status filters selected, return empty array (like documents)
    if (!selectedFilters.statusFilter || selectedFilters.statusFilter.length === 0) {
        return [];
    }

    let filtered = [...teams];

    // Text filter - search team name AND member names
    if (selectedFilters.textFilter) {
        const searchText = selectedFilters.textFilter.toLowerCase();

        filtered = filtered.map(team => {
            // Check team name (always search regardless of length)
            if (team.name?.toLowerCase().includes(searchText)) {
                return { ...team, _searchMatch: { matchType: 'team' } };
            }
            const matchingMembers = team.members?.filter(member => {
                const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
                return fullName.includes(searchText);
            }) || [];
            if (matchingMembers.length > 0) {
                return {
                    ...team,
                    _searchMatch: {
                        matchType: 'member',
                        memberNames: matchingMembers.map(m => `${m.first_name} ${m.last_name}`),
                        count: matchingMembers.length
                    }
                };
            }
            return null; // Doesn't match search
        }).filter(Boolean); // Remove nulls
    }

    // Status filter - 3 mutually exclusive states
    const statuses = selectedFilters.statusFilter;

    filtered = filtered.filter(team => {
        const isDraft = team.finalize_date === null;
        const isAuthorized = team.finalize_date !== null && team.is_authorized === true;
        const isNotAuthorized = team.finalize_date !== null && team.is_authorized === false;

        // Match any of the selected states
        return (
            (statuses.includes('DRAFT') && isDraft) ||
            (statuses.includes('AUTHORIZED') && isAuthorized) ||
            (statuses.includes('NOT_AUTHORIZED') && isNotAuthorized)
        );
    });

    return filtered;
};

/**
 * Sort teams alphabetically by name
 *
 * @param {Array} teams - Array of teams to sort
 * @returns {Array} Sorted array of teams
 */
export const sortTeamsByName = (teams = []) => {
    return [...teams].sort((a, b) => {
        const nameA = a.name?.toLowerCase() || '';
        const nameB = b.name?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
    });
};

export const FISCAL_CODE_REGEX = /^[a-zA-Z]{6}\d{2}[a-eA-EhlmprstHMLPRST]\d{2}[a-zA-Z]\d{3}[a-zA-Z]$/;

// Remove accents and special characters from a string and join with _
// Divide string by base characters and diacritics (accents) ([\u0300-\u036f])
// Example: "Sì" => "si"
export const snakeCase = string => {
    return string
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .replace(/[\W_]+/g, ' ')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');
};
